/*
Copyright (C) 2017  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import React from "react";
import { Link } from "react-router";
import { observer } from "mobx-react";
import styled, { css } from "styled-components";
import autobind from "autobind-decorator";
import { Info, LogOut, Monitor, Moon, Sun, UserRound } from "lucide-react";
// ChevronUp was the sidebar version's open/closed marker; the top-bar avatar has none.
// import { ChevronUp, Info, LogOut, UserRound } from "lucide-react";

import { ThemePalette } from "@src/components/Theme";
import { navigationMenu } from "@src/constants";
import type { User } from "@src/@types/User";

import configLoader from "@src/utils/Config";
import themeMode, { type ThemePreference } from "@src/utils/ThemeMode";

/*
 * The signed-in user, top right of every page as in the Tech9IQ shell: the avatar initial only (name
 * and email in its tooltip and menu). The menu: name and email, Your account, the theme (Light /
 * Dark / System, the shared t9_theme cookie), About SwiftIQ and Sign out.
 *
 * Before (sidebar foot): the signed-in user at the foot of the Swift sidebar, as in the Ops Console (sidebar__whoBtn and
 * profile-menu): avatar initial, name and role; the menu opens upwards with Your account (the
 * user's own page, where Users is available), About Swift and Sign out. `collapsed` is the folded
 * sidebar: the avatar alone.
 */
const Wrapper = styled.div<any>`
  position: relative;
`;
const Icon = styled.button<{ collapsed?: boolean; open?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: ${props => (props.open ? "var(--t9-bg-hover)" : "none")};
  font: inherit;
  text-align: left;
  cursor: pointer;
  color: ${ThemePalette.black};
  &:hover {
    background: var(--t9-bg-hover);
  }
  &:focus-visible {
    outline: 2px solid ${ThemePalette.accent};
    outline-offset: 1px;
  }
`;
const Avatar = styled.span`
  width: 28px;
  height: 28px;
  min-width: 28px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: ${ThemePalette.brandSoft};
  color: ${ThemePalette.primaryLight};
  font-size: 11px;
  font-weight: 600;
`;
/* Unused since the user moved to the top bar (initial only):
const Who = styled.span`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;
*/
/* Unused since the user moved to the top bar (initial only):
const WhoName = styled.span`
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  color: ${ThemePalette.black};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;
*/
const WhoRole = styled.span`
  display: block;
  margin-top: 2px;
  font-size: 11px;
  line-height: 16px;
  color: ${ThemePalette.grayscale[5]};
`;
/* Unused since the user moved to the top bar (initial only):
const Chevron = styled.span<{ open?: boolean }>`
  display: flex;
  color: ${ThemePalette.grayscale[3]};
  transition: transform 150ms;
  ${props =>
    props.open
      ? css`
          transform: rotate(180deg);
        `
      : ""}
`;
*/
// Was opening upwards from the sidebar foot (bottom: calc(100% + 6px); left: 0).
const List = styled.div<{ collapsed?: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: min(272px, calc(100vw - 24px));
  box-sizing: border-box;
  z-index: 500;
  padding: 6px;
  border: 1px solid ${ThemePalette.grayscale[0]};
  border-radius: 8px;
  background: var(--t9-bg-raised);
  box-shadow: var(--t9-shadow-xl);
`;
const ThemeRow = styled.div`
  padding: 6px 10px 8px 10px;
`;
const ThemeLabel = styled.div`
  font-size: 12px;
  line-height: 16px;
  color: ${ThemePalette.grayscale[5]};
  margin-bottom: 6px;
`;
const ThemeChoices = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2px;
  padding: 2px;
  border: 1px solid ${ThemePalette.grayscale[0]};
  border-radius: 6px;
  background: ${ThemePalette.grayscale[1]};
`;
const ThemeChoice = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 28px;
  padding: 0 4px;
  border: 0;
  border-radius: 4px;
  font: inherit;
  font-size: 12px;
  cursor: pointer;
  color: ${p => (p.active ? ThemePalette.black : ThemePalette.grayscale[5])};
  background: ${p => (p.active ? "var(--t9-bg-raised)" : "transparent")};
  box-shadow: ${p => (p.active ? "0 1px 2px rgb(0 0 0 / 0.12)" : "none")};
  font-weight: ${p => (p.active ? 500 : 400)};
  &:hover {
    color: ${ThemePalette.black};
  }
  &:focus-visible {
    outline: 2px solid ${ThemePalette.accent};
    outline-offset: 1px;
  }
`;
const THEME_CHOICES: { value: ThemePreference; label: string; icon: typeof Sun }[] = [
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
  { value: "system", label: "System", icon: Monitor },
];
const ListHeader = styled.div<any>`
  padding: 6px 10px 8px 10px;
  margin-bottom: 4px;
  border-bottom: 1px solid ${ThemePalette.grayscale[0]};
`;
const Username = styled(Link)`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: ${ThemePalette.black};
  text-decoration: none;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  ${props => (props.to === "" ? "pointer-events: none;" : "")}
  &:hover {
    color: ${ThemePalette.primary};
  }
`;
const Email = styled.div<any>`
  font-size: 12px;
  color: ${ThemePalette.grayscale[5]};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;
const ListItem = styled.div<any>``;
const Separator = styled.div`
  height: 1px;
  margin: 4px 0;
  background: ${ThemePalette.grayscale[0]};
`;
const itemCss = css<{ danger?: boolean }>`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border: 0;
  border-radius: 6px;
  background: none;
  font: inherit;
  font-size: 14px;
  text-align: left;
  text-decoration: none;
  color: ${ThemePalette.grayscale[4]};
  cursor: pointer;
  svg {
    color: ${ThemePalette.grayscale[3]};
    flex-shrink: 0;
  }
  &:hover,
  &:focus-visible {
    outline: none;
    background: var(--t9-bg-hover);
    color: ${props => (props.danger ? ThemePalette.alert : ThemePalette.black)};
    svg {
      color: ${props => (props.danger ? ThemePalette.alert : ThemePalette.grayscale[4])};
    }
  }
`;
const Label = styled.button<{ danger?: boolean }>`
  ${itemCss}
`;
const AccountLink = styled(Link)<{ danger?: boolean }>`
  ${itemCss}
`;
const NoUser = styled.div`
  padding: 8px 10px;
  font-size: 14px;
  color: ${ThemePalette.grayscale[5]};
`;

type DictItem = { label: React.ReactNode; value: string };
type Props = {
  onItemClick: (item: DictItem) => void;
  user: User | null | undefined;
  /** The folded sidebar: the avatar only, the menu opening to its right. */
  collapsed?: boolean;
  white?: boolean;
  className?: string;
};
type State = {
  showDropdownList: boolean;
};

export const roleLabel = (user: User | null | undefined) =>
  user && user.isAdmin ? "Administrator" : "Member";

@observer
class UserDropdown extends React.Component<Props, State> {
  state = {
    showDropdownList: false,
  };

  itemMouseDown: boolean | undefined;

  buttonRef = React.createRef<HTMLButtonElement>();

  listRef = React.createRef<HTMLDivElement>();

  componentDidMount() {
    window.addEventListener("mousedown", this.handlePageClick, false);
    window.addEventListener("keydown", this.handleKeyDown, false);
  }

  componentWillUnmount() {
    window.removeEventListener("mousedown", this.handlePageClick, false);
    window.removeEventListener("keydown", this.handleKeyDown, false);
  }

  get accountHref(): string | undefined {
    const user = this.props.user;
    if (!user || !user.isAdmin) return undefined;
    const usersPage = navigationMenu.find(
      m =>
        m.value === "users" &&
        !configLoader.config?.disabledPages.find(p => p === "users"),
    );
    return usersPage ? `/users/${user.id}` : undefined;
  }

  handleItemClick(item: DictItem) {
    if (this.props.onItemClick) {
      this.props.onItemClick(item);
    }

    this.setState({ showDropdownList: false });
  }

  @autobind
  handlePageClick() {
    if (!this.itemMouseDown) {
      this.setState({ showDropdownList: false });
    }
  }

  @autobind
  handleKeyDown(e: KeyboardEvent) {
    if (!this.state.showDropdownList) return;
    if (e.key === "Escape") {
      this.setState({ showDropdownList: false });
      this.buttonRef.current?.focus();
      return;
    }
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    const items = Array.from(
      this.listRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') || [],
    );
    if (!items.length) return;
    e.preventDefault();
    const i = items.indexOf(document.activeElement as HTMLElement);
    const next = (i + (e.key === "ArrowDown" ? 1 : -1) + items.length) % items.length;
    items[next].focus();
  }

  handleButtonClick() {
    this.setState(
      prevState => ({ showDropdownList: !prevState.showDropdownList }),
      () => {
        if (this.state.showDropdownList) {
          this.listRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
        }
      },
    );
  }

  renderList() {
    if (!this.state.showDropdownList) {
      return null;
    }
    const user = this.props.user;
    const href = this.accountHref;
    const items: (DictItem & { icon: typeof Info })[] = [
      { label: "About SwiftIQ", value: "about", icon: Info },
      { label: "Sign out", value: "signout", icon: LogOut },
    ];
    const keepOpen = {
      onMouseDown: () => {
        this.itemMouseDown = true;
      },
      onMouseUp: () => {
        this.itemMouseDown = false;
      },
    };

    return (
      <List
        ref={this.listRef}
        role="menu"
        aria-label="Account"
        collapsed={this.props.collapsed}
        id="swift-user-menu"
        {...keepOpen}
      >
        {user ? (
          <ListHeader>
            <Username to={href || ""} tabIndex={-1}>
              {user.name}
            </Username>
            <Email>{user.email}</Email>
            <WhoRole>{roleLabel(user)}</WhoRole>
          </ListHeader>
        ) : (
          <NoUser>No signed in user</NoUser>
        )}
        {user && href ? (
          <ListItem>
            <AccountLink
              role="menuitem"
              to={href}
              onClick={() => this.setState({ showDropdownList: false })}
            >
              <UserRound size={16} strokeWidth={1.75} aria-hidden="true" />
              Your account
            </AccountLink>
          </ListItem>
        ) : null}
        {user ? (
          <ThemeRow role="group" aria-label="Theme">
            <ThemeLabel>Theme</ThemeLabel>
            <ThemeChoices>
              {THEME_CHOICES.map(c => (
                <ThemeChoice
                  key={c.value}
                  type="button"
                  role="menuitemradio"
                  aria-checked={themeMode.preference === c.value}
                  active={themeMode.preference === c.value}
                  onClick={() => themeMode.set(c.value)}
                >
                  <c.icon size={13} strokeWidth={2} aria-hidden="true" />
                  {c.label}
                </ThemeChoice>
              ))}
            </ThemeChoices>
          </ThemeRow>
        ) : null}
        {user
          ? items.map(item => (
              <ListItem key={item.value}>
                {item.value === "signout" ? <Separator role="separator" /> : null}
                <Label
                  type="button"
                  role="menuitem"
                  danger={item.value === "signout"}
                  onClick={() => {
                    this.handleItemClick(item);
                  }}
                >
                  <item.icon size={16} strokeWidth={1.75} aria-hidden="true" />
                  {item.label}
                </Label>
              </ListItem>
            ))
          : null}
      </List>
    );
  }

  render() {
    const user = this.props.user;
    const name = user ? user.name || user.email || "" : "";
    const initial = (name.trim().slice(0, 1) || "?").toUpperCase();
    const open = this.state.showDropdownList;
    return (
      <Wrapper className={this.props.className}>
        {this.renderList()}
        <Icon
          ref={this.buttonRef}
          type="button"
          collapsed={this.props.collapsed}
          open={open}
          data-fold="center"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={open ? "swift-user-menu" : undefined}
          aria-label={`Account: ${name || "not signed in"}`}
          title={user ? [name, user.email].filter((v, i, a) => v && a.indexOf(v) === i).join("\n") : "Account"}
          onMouseDown={() => {
            this.itemMouseDown = true;
          }}
          onMouseUp={() => {
            this.itemMouseDown = false;
          }}
          onClick={() => this.handleButtonClick()}
        >
          <Avatar aria-hidden="true">{initial}</Avatar>
          {/* Was the name and role, with a chevron, beside the avatar (the sidebar foot); the Tech9IQ
              shell shows the initial only, and the role moved into the menu's header:
          <Who data-fold="hide"><WhoName>{name || "Not signed in"}</WhoName><WhoRole>{roleLabel(user)}</WhoRole></Who>
          <Chevron open={open} aria-hidden="true" data-fold="hide"><ChevronUp size={14} strokeWidth={2} /></Chevron> */}
        </Icon>
      </Wrapper>
    );
  }
}

export default UserDropdown;
